import { type FormEvent, type ReactElement, useState } from 'react';
import { Link } from 'react-router';
import { Button, buttonVariants, cn, FormField } from '@/shared/ui';
import { useResetPassword } from '../model/use-password-reset';
import { SUBMIT_BUTTON_CLASS, UNDERLINE_INPUT_CLASS } from './auth-form-styles';
import { SubmitArrow } from './submit-arrow';

interface ResetPasswordFormProps {
  /** 메일 링크(/reset-password?token=...)로 전달된 재설정 토큰. */
  readonly token: string;
}

/** 새 비밀번호 설정 폼 — 성공 시 기존 로그인이 전부 해제된다. */
export const ResetPasswordForm = ({ token }: ResetPasswordFormProps): ReactElement => {
  const [password, setPassword] = useState('');
  const { passwordError, formError, isPending, isDone, submit } = useResetPassword();

  if (isDone) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-[15px] text-muted-foreground">
          비밀번호가 변경되었습니다. 보안을 위해 기존 로그인은 전부 해제되었습니다.
        </p>
        <Link to="/sign-in" className={cn(buttonVariants(), SUBMIT_BUTTON_CLASS)}>
          새 비밀번호로 로그인
          <SubmitArrow />
        </Link>
      </div>
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit({ token, password });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
      <FormField
        label="새 비밀번호"
        type="password"
        autoComplete="new-password"
        description="10자 이상, 대문자 · 소문자 · 숫자를 포함해야 합니다."
        isRequired
        className={UNDERLINE_INPUT_CLASS}
        value={password}
        error={passwordError ?? undefined}
        onChange={(event) => setPassword(event.target.value)}
      />

      {formError ? (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      ) : null}

      <Button type="submit" isLoading={isPending} className={SUBMIT_BUTTON_CLASS}>
        비밀번호 변경
        <SubmitArrow />
      </Button>
    </form>
  );
};
